import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { useResultHandler } from "../../../hooks/useResultHandler";
import { getStatisticsNotification } from "../api/notification.api";
import type {
  GetNotificationStatisticsRequest,
  NotificationStatisticResponse,
} from "../types/notification.types";

function createNotificationStatisticsRequest(): GetNotificationStatisticsRequest {
  const now = new Date();
  const dayOfWeek = now.getDay() || 7;
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek + 1);
  startOfWeek.setHours(0, 0, 0, 0);

  const startOfNextWeek = new Date(startOfWeek);
  startOfNextWeek.setDate(startOfWeek.getDate() + 7);

  return { from: startOfWeek.toISOString(), to: startOfNextWeek.toISOString() };
}

export const useGetStatisticsNotification = () => {
  const [statistics, setStatistics] = useState<NotificationStatisticResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { run: runLatestRequest, loading } = useLatestRequest();
  const { handleError } = useResultHandler();

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const result = await runLatestRequest((signal) =>
        getStatisticsNotification(createNotificationStatisticsRequest(), signal),
      );
      if (result !== undefined) {
        setStatistics(result);
        return result;
      }
    } catch (fetchError) {
      setStatistics(null);
      setError(handleError(fetchError, "Failed to load notification statistics"));
      return undefined;
    }
  }, [handleError, runLatestRequest]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { statistics, loading, error, refresh };
};
